import * as React from 'react';
import { Action, any, async, AsyncState, Fun, HttpResult, IOWidget, loadingAsyncState, mkWidget, stateful } from "widgets-for-react";
import { CookieSensitiveContentWrapper } from './cms/blocks/cookiebar.mod';
import { DataLayer } from './public_site/utils/datalayer';

type VideoMetadata = {
  kind: 'youtube' | 'vimeo'
  imageUrl: string
  videoUrl: string
  title: string
}

type VideoState = {
  metadata: AsyncState<VideoMetadata>
  showStill: boolean
  showPlayer: boolean
}

export type VideoProps = {
  videoId: string
  provider: 'youtube' | 'vimeo'
  width: number
  height: number
  transitionDelay?: number
  useCookiebar?: boolean
}

export const video =
  (default_placeholder = '') : IOWidget<VideoProps, VideoState> => props => stateful<VideoState>()(
    s0 => any<Action<VideoState>>()([
      async<VideoMetadata>()(s0.metadata).map<Action<VideoState>>(a => s1 => ({ ...s1, metadata: a(s1.metadata) })),
      mkWidget({
        run: setState => (
          s0.metadata.kind !== 'loaded' ? null : (
            <>
              {s0.showPlayer && (
                <iframe
                  src={s0.metadata.value.videoUrl + '?autoplay=1'}
                  allowTransparency={true}
                  allowFullScreen={true}
                  frameBorder={0}
                  allow="autoplay;"
                />
              )} 

              {s0.showStill && (
                <button
                  onClick={() => {
                    setState(s1 => ({ ...s1, showPlayer: true }))
                    setTimeout(
                      () => setState(s1 => ({ ...s1, showStill: false })),
                      props.transitionDelay ? props.transitionDelay : 750
                    )
                    DataLayer.playVideo()
                  }}
                  className="video-wrapper__still"
                >
                  <img
                    src={s0.metadata.value.imageUrl || default_placeholder}
                    alt={s0.metadata.value.title}
                  />
                  <svg viewBox="0 0 66 66" className="video-wrapper__icon" aria-hidden="true"><g fill="#F8F8F8"><circle cx="33" cy="33" r="33" opacity="0.1" className="c1"/><circle cx="33" cy="33" r="27.5" opacity="0.3" className="c2" /><circle cx="33" cy="33" r="22"/></g><path d="M39.4 31.6L30.8 26.2C30.1 25.7 29 26.2 29 27.2L29 38.1C29 39.1 30 39.7 30.8 39.2L39.4 33.8C40.2 33.3 40.2 32.1 39.4 31.6Z" fill="#0077C8"/></svg>
                </button>
              )}
            </>
          )
        )
      })
    ]).map(a => a(s0))
  )({
    metadata: loadingAsyncState(() => fetchVideoMetadata(props)),
    showStill: true,
    showPlayer: false
  }).wrapHTML(video => props.useCookiebar !== false ? (
    // TODO: Clean up this video-wrapper div, add this to the CookieSensitiveContentWrapper
    // TODO: Don't add classNames directly but change this to options
    <div className="video-wrapper">
      <CookieSensitiveContentWrapper
        options={{ style: {
          cookies_classname: 'cookie-notice block',
          cookies_inner_classname: 'text--styled',
          cookies_clickable_classname: 'button--secondary'
        } }}
        placeholder={<>Deze video kun je alleen bekijken als je de cookies accepteert.</>}
      >
        {video}
      </CookieSensitiveContentWrapper>
    </div>
  ) : (
    <div className="video-wrapper">
      {video}
    </div>
  ))

const fetchVideoMetadata: Fun<VideoProps, Promise<HttpResult<VideoMetadata>>> =
  props => props.provider == "youtube"
    ? fetchYoutubeVideoMetadata(props)
    : fetchVimeoVideoMetadata(props)

type OembedResponse = {
  title: string,
  description: string,
  thumbnail_url: string,
  thumnail_height: number,
  thumbnail_width: number
}

const fetchVimeoVideoMetadata: Fun<VideoProps, Promise<HttpResult<VideoMetadata>>> =
  props => new Promise(async res => {
    try {
      let metadata: OembedResponse = await fetch(`/wp-json/wp/v2/video-oembed?url=https://vimeo.com/${props.videoId}&width=${props.width}&height=${props.height}`).then(res => res.json())

      res({
        kind: 'result',
        status: 200,
        value: {
          kind: 'vimeo',
          imageUrl: metadata.thumbnail_url,
          videoUrl: `https://player.vimeo.com/video/${props.videoId}`,
          title: metadata.title
        }
      })
    } catch (e) {
      console.error(e)
      res({ kind: 'failed', status: 500 })
    }
  })

const fetchYoutubeVideoMetadata: Fun<VideoProps, Promise<HttpResult<VideoMetadata>>> =
  props => new Promise(async res => {
    try {
      let metadata: OembedResponse = await fetch(`/wp-json/wp/v2/video-oembed?url=https://www.youtube.com/watch?v=${props.videoId}&width=${props.width}&height=${props.height}`).then(res => res.json())

      res({
        kind: 'result',
        status: 200,
        value: {
          kind: 'youtube',
          imageUrl: `https://img.youtube.com/vi/${props.videoId}/maxresdefault.jpg`,
          videoUrl: `https://www.youtube.com/embed/${props.videoId}`,
          title: metadata.title
        }
      })
    } catch (e) {
      console.error(e)
      res({ kind: 'failed', status: 500 })
    }
  })

